<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("MAIL_V2.MAIL_LAYOUTS") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openCreateModal"
            >
              <span class="btn-inner--icon"><i class="far fa-plus-circle"></i></span>
              <span class="btn-inner--text">{{ $t("MAIL_V2.ADD_LAYOUT") }}</span>
            </base-button>
          </div>
        </div>
      </div>

      <!-- Layout list -->
      <div class="page-wrapper-table" v-if="!isEditorOpened">
        <div class="page-wrapper-table-header">
          <el-select
            class="select-primary pagination-select mb-2"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>

          <reseller-selector
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_RESELLERS)"
            :filterable="true"
            :showAll="false"
            :allowNone="true"
            @resellerChanged="(id) => { selectedReseller = id; loadLayouts(); }"
          />

          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH')"
            clearable
          />
        </div>

        <div class="page-wrapper-table-body">
          <div class="page-wrapper-table-body-inner">
            <el-table
              class="align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="filteredLayouts"
            >
              <div class="table-loading" slot="empty" v-if="loading">
                <img src="/img/loading.gif" />
              </div>

              <el-table-column :label="$t('COMMON.NAME')" min-width="220">
                <template v-slot="{ row }">
                  <div>
                    <strong>{{ row.name }}</strong>
                    <br />
                    <span class="text-muted" style="font-size:11px;">{{ row.reseller_name || '—' }}</span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.SOURCE')" min-width="130">
                <template v-slot="{ row }">
                  <span class="badge-cat" :class="'badge-cat--' + row.source">
                    {{ row.source.toUpperCase() }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.STATUS')" min-width="140">
                <template v-slot="{ row }">
                  <span v-if="row.is_default" class="badge-st badge-st--default">{{ $t("MAIL_V2.DEFAULT") }}</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('COMMON.UPDATED_AT')" min-width="150">
                <template v-slot="{ row }">
                  <span>{{ row.updated_at | moment("DD/MM/YYYY HH:mm") }}</span>
                </template>
              </el-table-column>

              <el-table-column fixed="right" min-width="80">
                <div slot-scope="{ row }" class="table-actions">
                  <el-tooltip :content="$t('COMMON.EDIT')" placement="top">
                    <a type="text" @click="editLayout(row)" class="table-action" style="cursor:pointer;">
                      <i class="fal fa-edit"></i>
                    </a>
                  </el-tooltip>
                </div>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>

      <!-- Editor -->
      <div v-if="isEditorOpened" class="resizable-wrapper show">
        <div class="resizable-wrapper-content w-100" v-resizable-modal="'MAIL_LAYOUT'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>{{ $t("MAIL_V2.EDIT_LAYOUT") }} — {{ editingLayout?.name }}</h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeEditor"><i class="fal fa-times"></i></button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body" style="display:block !important;">
            <form class="add-form" @submit.prevent="saveLayout" @keydown.enter.prevent="() => {}">
              <div class="form-wrapper">
                <base-input
                  :label="$t('MAIL_V2.LAYOUT_NAME')"
                  v-model="layoutForm.name"
                />
              </div>

              <div class="form-wrapper">
                <base-input :label="$t('MAIL_V2.PREVIEW_WITH_ORG')">
                  <organization-selector
                    :filterable="true"
                    :showAll="false"
                    :allowNone="true"
                    @organizationChanged="onPreviewOrgChanged"
                  />
                </base-input>
              </div>

              <div class="form-wrapper full">
                <div class="layout-editor-split">
                  <div class="layout-editor-code">
                    <label class="mb-1"><i class="fal fa-code"></i> HTML</label>
                    <textarea
                      class="form-control layout-textarea"
                      v-model="layoutForm.body_html"
                      rows="30"
                      spellcheck="false"
                    ></textarea>
                    <p class="text-muted mt-1" style="font-size:11px;">
                      {{ $t("MAIL_V2.LAYOUT_SLOTS_HELP") }}
                    </p>
                  </div>
                  <div class="layout-editor-preview">
                    <label class="mb-1">
                      <i class="fal fa-eye"></i> {{ $t("MAIL_V2.PREVIEW") }}
                      <a @click="refreshPreview" class="ml-2" style="cursor:pointer;font-size:12px;color:#0091ca;">
                        <i class="fal fa-sync-alt"></i> {{ $t("MAIL_V2.PREVIEW") }}
                      </a>
                    </label>
                    <div class="layout-preview-frame-wrapper">
                      <iframe
                        ref="previewFrame"
                        class="layout-preview-frame"
                        sandbox="allow-same-origin"
                        :srcdoc="previewHtml || emptyPreviewHtml"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>

              <div class="submit-wrapper">
                <base-button
                  type="button"
                  class="btn btn-sm elite-submit"
                  native-type="submit"
                  :disabled="saving"
                >
                  <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                  {{ $t("COMMON.SAVE") }}
                </base-button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <!-- Create Layout Drawer -->
      <div v-if="isCreateOpened" class="resizable-wrapper show" :key="'create-' + renderKey">
        <div class="resizable-wrapper-content" v-resizable-modal="'MAIL_LAYOUT_CREATE'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1><i class="fal fa-plus-circle mr-2"></i> {{ $t("MAIL_V2.ADD_LAYOUT") }}</h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="isCreateOpened = false"><i class="fal fa-times"></i></button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <form class="add-form" @submit.prevent="submitCreate" @keydown.enter.prevent="() => {}">
              <div class="form-wrapper"
                v-if="$currentUserCan($permissions.PERM_VIEW_ANY_RESELLERS)"
              >
                <base-input
                  :label="`${$t('COMMON.RESELLER')} (*)`"
                  :placeholder="$t('COMMON.RESELLER')"
                >
                  <reseller-selector
                    :filterable="true"
                    :showAll="false"
                    @resellerChanged="(id) => { createForm.reseller_id = id; }"
                  />
                </base-input>
              </div>

              <div class="form-wrapper">
                <base-input
                  :label="`${$t('COMMON.NAME')} (*)`"
                  :placeholder="$t('COMMON.NAME')"
                  v-model="createForm.name"
                />
              </div>

              <div class="form-wrapper">
                <base-input :label="$t('MAIL_V2.SET_AS_DEFAULT')">
                  <el-switch v-model="createForm.is_default" />
                </base-input>
              </div>

              <div class="submit-wrapper">
                <base-button
                  type="button"
                  class="btn btn-sm elite-submit"
                  native-type="submit"
                  :disabled="createLoading"
                >
                  <i class="fas fa-spinner fa-spin" v-if="createLoading"></i>
                  {{ $t("MAIL_V2.ADD_LAYOUT") }}
                </base-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Tooltip, Switch, Select, Option } from "element-ui";
import ResellerSelector from "@/components/ResellerSelector.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tooltip.name]: Tooltip,
    [Switch.name]: Switch,
    [Select.name]: Select,
    [Option.name]: Option,
    ResellerSelector,
    OrganizationSelector,
  },

  data() {
    return {
      loading: false,
      saving: false,
      createLoading: false,
      query: "",
      layouts: [],
      pagination: {
        perPage: 20,
        perPageOptions: [10, 20, 50],
      },
      selectedReseller: null,
      isEditorOpened: false,
      isCreateOpened: false,
      renderKey: 1,
      createForm: {
        reseller_id: null,
        name: "",
        is_default: false,
      },
      editingLayout: null,
      previewOrgId: null,
      previewHtml: "",
      emptyPreviewHtml: '<p style="color:#999;text-align:center;padding:60px;">Cliquez sur Rafraîchir pour voir le rendu</p>',
      layoutForm: {
        name: "",
        body_html: "",
        body_json: null,
      },
    };
  },

  computed: {
    filteredLayouts() {
      if (!this.query) return this.layouts;
      const q = this.query.toLowerCase();
      return this.layouts.filter((l) =>
        l.name?.toLowerCase().includes(q) || l.source?.toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    this.loadLayouts();
  },

  methods: {
    async loadLayouts() {
      this.loading = true;
      try {
        const params = {};
        if (this.selectedReseller) {
          params.filter = { reseller_id: this.selectedReseller };
        }
        this.layouts = await this.$store.dispatch("mailV2/listLayouts", { params });
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.createForm = { reseller_id: null, name: "", is_default: false };
      this.isCreateOpened = true;
      this.renderKey++;
    },

    async submitCreate() {
      if (!this.createForm.name || !this.createForm.name.trim()) {
        this.$notify({ type: "danger", message: this.$t("MAIL_V2.FIELD_REQUIRED") });
        return;
      }
      this.createLoading = true;
      try {
        const result = await this.$store.dispatch("mailV2/createLayout", { data: this.createForm });
        this.isCreateOpened = false;
        await this.loadLayouts();
        this.$notify({ type: "success", message: this.$t("MAIL_V2.LAYOUT_CREATED") });

        // Open editor for new layout
        if (result?.id) {
          const newLayout = this.layouts.find((l) => l.id === result.id);
          if (newLayout) {
            this.editLayout(newLayout);
          } else {
            // Fallback: open directly with the result data
            this.editLayout({ id: result.id, name: result.name });
          }
        }
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t("ERRORS.SOMETHING_WENT_WRONG");
        this.$notify({ type: "danger", message: msg });
      } finally {
        this.createLoading = false;
      }
    },

    async editLayout(layout) {
      try {
        const full = await this.$store.dispatch("mailV2/getLayout", { layoutId: layout.id });
        this.editingLayout = layout;
        this.layoutForm = {
          name: full.name || "",
          body_html: full.body_html || "",
          body_json: full.body_json || null,
        };
        this.previewHtml = "";
        this.isEditorOpened = true;
        this.$nextTick(() => this.refreshPreview());
      } catch (error) {
        this.$notify({ type: "danger", message: this.$t("ERRORS.SOMETHING_WENT_WRONG") });
      }
    },

    closeEditor() {
      this.isEditorOpened = false;
      this.editingLayout = null;
      this.previewHtml = "";
    },

    async saveLayout() {
      this.saving = true;
      try {
        await this.$store.dispatch("mailV2/updateLayout", {
          layoutId: this.editingLayout.id,
          data: this.layoutForm,
        });
        this.$notify({ type: "success", message: this.$t("MAIL_V2.LAYOUT_SAVED") });
        this.loadLayouts();
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t("ERRORS.SOMETHING_WENT_WRONG");
        this.$notify({ type: "danger", message: msg });
      } finally {
        this.saving = false;
      }
    },

    onPreviewOrgChanged(orgId) {
      this.previewOrgId = orgId;
      this.refreshPreview();
    },

    async refreshPreview() {
      if (!this.editingLayout) return;
      try {
        const result = await this.$store.dispatch("mailV2/previewLayout", {
          layoutId: this.editingLayout.id,
          data: {
            organization_id: this.previewOrgId || null,
            sample_content: null,
          },
        });
        this.previewHtml = result.html || "";
      } catch (error) {
        this.previewHtml = '<p style="color:red;padding:20px;">Erreur: ' + (error?.response?.data?.message || 'unknown') + '</p>';
      }
    },
  },
};
</script>

<style scoped>
.badge-cat { padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.badge-cat--platform { background: #e3f2fd; color: #1565c0; }
.badge-cat--editor { background: #fff3e0; color: #e65100; }
.badge-cat--upload { background: #f3e5f5; color: #7b1fa2; }
.badge-cat--sync { background: #e8f5e9; color: #2e7d32; }

.badge-st { padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; }
.badge-st--default { background: #e8f5e9; color: #2e7d32; }

.layout-editor-split {
  display: flex;
  gap: 16px;
  min-height: 600px;
}

.layout-editor-code {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.layout-editor-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.layout-textarea {
  flex: 1;
  font-family: 'Fira Code', 'SF Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;
  min-height: 500px;
  background: #1e1e2e;
  color: #cdd6f4;
  border-radius: 8px;
  padding: 16px;
  border: none;
}

.layout-preview-frame-wrapper {
  flex: 1;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.layout-preview-frame {
  width: 100%;
  min-height: 500px;
  border: none;
}
</style>
